/**
 * Trust Centre answer-once / external-portal — strings.
 *
 * SAME SELF-REGISTERING PATTERN AS craStrings / incidentStrings.
 *   TRUST_PORTAL_STRINGS     English; also the inline `defaultValue` of every
 *                            lookup, so the screens render correctly before the
 *                            locale JSON is updated.
 *   TRUST_PORTAL_STRINGS_EL  Greek.
 * `registerTrustPortalTranslations()` adds both to the running i18n instance
 * without touching i18n/index.ts or the locale JSON, so the parity guard (which
 * reads the JSON) is unaffected. Once these are merged into en.json/el.json the
 * registration is redundant but harmless — `addResources` never overwrites a
 * key already present.
 *
 * Covers BOTH new audiences: the tenant admin (Answers + External access tabs)
 * and the external portal visitor (login, NDA clickwrap, browse).
 */

import i18n from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const TRUST_PORTAL_STRINGS = {
  // --- Admin: tabs ---------------------------------------------------------
  'trust.tab.publish': 'Public page',
  'trust.tab.answers': 'Answers',
  'trust.tab.access': 'External access',

  // --- Admin: answers ------------------------------------------------------
  'trust.answers.title': 'Reverse replies',
  'trust.answers.subtitle':
    'Pre-answer the compliance questions you get asked repeatedly, once. Tag each answer with who may see it, publish it, and give external parties gated, read-only access instead of filling in their questionnaires.',
  'trust.answers.add': 'Add answer',
  'trust.answers.seed': 'Add starter answers',
  'trust.answers.seedHint':
    'Create draft answers from our own supplier-questionnaire topics (MFA, encryption, incident response, and more) for you to tailor and publish.',
  'trust.answers.seeded_one': '{{count}} starter answer added as a draft.',
  'trust.answers.seeded_other': '{{count}} starter answers added as drafts.',
  'trust.answers.seededNone': 'All starter answers are already present.',
  'trust.answers.empty': 'No reverse replies yet. Add your own, or start from our templates.',
  'trust.answers.loadFailed': 'The reverse replies could not be loaded.',
  'trust.answers.question': 'Question',
  'trust.answers.answer': 'Answer',
  'trust.answers.category': 'Category',
  'trust.answers.categoryHint': 'Optional grouping, e.g. “Access control”.',
  'trust.answers.tier': 'Who can see this',
  'trust.answers.source': 'Source',
  'trust.answers.published': 'Published',
  'trust.answers.draft': 'Draft',
  'trust.answers.publish': 'Published (visible to permitted external users)',
  'trust.answers.save': 'Save',
  'trust.answers.saving': 'Saving…',
  'trust.answers.cancel': 'Cancel',
  'trust.answers.edit': 'Edit',
  'trust.answers.delete': 'Delete',
  'trust.answers.deleteConfirm': 'Delete this reverse reply? This cannot be undone.',
  'trust.answers.saved': 'Saved.',

  // --- Tiers (shared wording, admin + portal) ------------------------------
  'trust.tier.PUBLIC': 'Public',
  'trust.tier.NDA': 'Under NDA',
  'trust.tier.NAMED': 'Named customers',
  'trust.tier.PUBLIC.hint': 'Anyone with portal access can read it.',
  'trust.tier.NDA.hint': 'Readable only after the viewer accepts the NDA.',
  'trust.tier.NAMED.hint': 'Readable only by viewers you grant named-customer access.',

  // --- Admin: external users ----------------------------------------------
  'trust.access.title': 'External access',
  'trust.access.subtitle':
    'Invite external parties to a gated, read-only portal, or review and approve the requests that come in. Access expires automatically.',
  'trust.access.usersTitle': 'External users',
  'trust.access.invite': 'Invite external user',
  'trust.access.usersEmpty': 'No external users yet.',
  'trust.access.email': 'E-mail',
  'trust.access.name': 'Name',
  'trust.access.company': 'Company',
  'trust.access.role': 'Role',
  'trust.access.expiresInDays': 'Access lasts (days)',
  'trust.access.namedAccess': 'Grant named-customer access',
  'trust.access.namedAccessHint': 'Lets this viewer see the most sensitive, named-customer-only answers.',
  'trust.access.status': 'Status',
  'trust.access.expires': 'Expires',
  'trust.access.lastLogin': 'Last sign-in',
  'trust.access.nda': 'NDA',
  'trust.access.ndaAccepted': 'Accepted {{date}}',
  'trust.access.ndaPending': 'Not accepted',
  'trust.access.revoke': 'Revoke',
  'trust.access.revokeConfirm': 'Revoke this user’s access? They will be signed out immediately.',
  'trust.access.save': 'Save',
  'trust.access.create': 'Create',
  'trust.access.cancel': 'Cancel',
  'trust.access.statusINVITED': 'Invited',
  'trust.access.statusACTIVE': 'Active',
  'trust.access.statusREVOKED': 'Revoked',
  'trust.access.statusExpired': 'Expired',
  'trust.access.role.CLIENT': 'Client',
  'trust.access.role.PARTNER': 'Partner',
  'trust.access.role.AUDITOR': 'Auditor',
  'trust.access.passwordTitle': 'Initial password',
  'trust.access.passwordBody':
    'Share this password with {{email}} out-of-band (it is shown only once). They sign in at the portal link below.',
  'trust.access.passwordDone': 'Done',
  'trust.access.portalLink': 'Portal link',
  'trust.access.usersLoadFailed': 'The external users could not be loaded.',

  // --- Admin: access requests ---------------------------------------------
  'trust.access.requestsTitle': 'Access requests',
  'trust.access.requestsEmpty': 'No pending access requests.',
  'trust.access.requestReason': 'Reason',
  'trust.access.approve': 'Approve',
  'trust.access.deny': 'Deny',
  'trust.access.denyConfirm': 'Deny this access request?',
  'trust.access.approveTitle': 'Approve request',
  'trust.access.requested': 'Requested {{date}}',
  'trust.access.error.TRUST_EXTERNAL_USER_EXISTS': 'An external user with that e-mail already exists.',
  'trust.access.error.UNKNOWN': 'Something went wrong. Try again.',

  // --- Public page: request access ----------------------------------------
  'trust.request.title': 'Request access',
  'trust.request.body':
    'Need more detail than this page shows? Request gated access to this organisation’s pre-answered security questions.',
  'trust.request.email': 'Your work e-mail',
  'trust.request.company': 'Your company',
  'trust.request.reason': 'What do you need access for? (optional)',
  'trust.request.submit': 'Request access',
  'trust.request.submitting': 'Sending…',
  'trust.request.sent':
    'Thanks. If access is granted, you will receive an invitation by e-mail.',
  'trust.request.failed': 'Your request could not be sent. Try again later.',
  'trust.request.open': 'Already have access? Sign in',

  // --- External portal -----------------------------------------------------
  'trust.portal.title': 'Security portal',
  'trust.portal.signIn': 'Sign in',
  'trust.portal.signingIn': 'Signing in…',
  'trust.portal.email': 'E-mail',
  'trust.portal.password': 'Password',
  'trust.portal.signInFailed': 'That e-mail or password was not recognised, or your access has ended.',
  'trust.portal.signOut': 'Sign out',
  'trust.portal.welcome': 'You are viewing {{name}}’s security answers.',
  'trust.portal.loadFailed': 'The portal could not be loaded.',
  'trust.portal.answersTitle': 'Pre-answered questions',
  'trust.portal.answersEmpty': 'There are no answers available to you yet.',
  'trust.portal.documentsTitle': 'Published posture',
  'trust.portal.noDocuments': 'Nothing is published on the public page.',
  'trust.portal.expiresOn': 'Your access expires {{date}}.',
  'trust.portal.readOnly': 'Read-only. These answers are provided in confidence.',

  // --- NDA clickwrap -------------------------------------------------------
  'trust.portal.ndaTitle': 'Confidentiality agreement',
  'trust.portal.ndaBody':
    'Some answers are shared in confidence. By continuing you agree not to disclose them outside your organisation and to use them only to assess this organisation as a supplier or partner.',
  'trust.portal.ndaAccept': 'I agree',
  'trust.portal.ndaLater': 'Not now',
  'trust.portal.ndaUnlocks': 'Accept the confidentiality agreement to see additional answers.',
  'trust.portal.ndaAccepted': 'Confidentiality agreement accepted.',
} as const;

export const TRUST_PORTAL_STRINGS_EL: Record<keyof typeof TRUST_PORTAL_STRINGS, string> = {
  'trust.tab.publish': 'Δημόσια σελίδα',
  'trust.tab.answers': 'Απαντήσεις',
  'trust.tab.access': 'Εξωτερική πρόσβαση',

  'trust.answers.title': 'Προ-απαντήσεις',
  'trust.answers.subtitle':
    'Απαντήστε μία φορά στις ερωτήσεις συμμόρφωσης που σας ρωτούν συνεχώς. Χαρακτηρίστε κάθε απάντηση με το ποιος μπορεί να τη δει, δημοσιεύστε την και δώστε σε εξωτερικά μέρη ελεγχόμενη πρόσβαση μόνο για ανάγνωση.',
  'trust.answers.add': 'Προσθήκη απάντησης',
  'trust.answers.seed': 'Προσθήκη αρχικών απαντήσεων',
  'trust.answers.seedHint':
    'Δημιουργία προσχεδίων από τα θέματα του δικού μας ερωτηματολογίου προμηθευτών, για να τα προσαρμόσετε και να τα δημοσιεύσετε.',
  'trust.answers.seeded_one': 'Προστέθηκε {{count}} αρχική απάντηση ως προσχέδιο.',
  'trust.answers.seeded_other': 'Προστέθηκαν {{count}} αρχικές απαντήσεις ως προσχέδια.',
  'trust.answers.seededNone': 'Όλες οι αρχικές απαντήσεις υπάρχουν ήδη.',
  'trust.answers.empty': 'Δεν υπάρχουν προ-απαντήσεις ακόμη. Προσθέστε δικές σας ή ξεκινήστε από τα πρότυπα.',
  'trust.answers.loadFailed': 'Δεν ήταν δυνατή η φόρτωση των προ-απαντήσεων.',
  'trust.answers.question': 'Ερώτηση',
  'trust.answers.answer': 'Απάντηση',
  'trust.answers.category': 'Κατηγορία',
  'trust.answers.categoryHint': 'Προαιρετική ομαδοποίηση, π.χ. «Έλεγχος πρόσβασης».',
  'trust.answers.tier': 'Ποιος μπορεί να το δει',
  'trust.answers.source': 'Πηγή',
  'trust.answers.published': 'Δημοσιευμένη',
  'trust.answers.draft': 'Προσχέδιο',
  'trust.answers.publish': 'Δημοσιευμένη (ορατή στους επιτρεπόμενους εξωτερικούς χρήστες)',
  'trust.answers.save': 'Αποθήκευση',
  'trust.answers.saving': 'Αποθήκευση…',
  'trust.answers.cancel': 'Άκυρο',
  'trust.answers.edit': 'Επεξεργασία',
  'trust.answers.delete': 'Διαγραφή',
  'trust.answers.deleteConfirm': 'Διαγραφή αυτής της προ-απάντησης; Δεν αναιρείται.',
  'trust.answers.saved': 'Αποθηκεύτηκε.',

  'trust.tier.PUBLIC': 'Δημόσιο',
  'trust.tier.NDA': 'Υπό NDA',
  'trust.tier.NAMED': 'Επώνυμοι πελάτες',
  'trust.tier.PUBLIC.hint': 'Το διαβάζει όποιος έχει πρόσβαση στην πύλη.',
  'trust.tier.NDA.hint': 'Διαβάζεται μόνο αφού ο χρήστης αποδεχθεί το NDA.',
  'trust.tier.NAMED.hint': 'Διαβάζεται μόνο από χρήστες στους οποίους δίνετε πρόσβαση επώνυμου πελάτη.',

  'trust.access.title': 'Εξωτερική πρόσβαση',
  'trust.access.subtitle':
    'Προσκαλέστε εξωτερικά μέρη σε μια ελεγχόμενη πύλη μόνο για ανάγνωση, ή εξετάστε και εγκρίνετε τα αιτήματα που φτάνουν. Η πρόσβαση λήγει αυτόματα.',
  'trust.access.usersTitle': 'Εξωτερικοί χρήστες',
  'trust.access.invite': 'Πρόσκληση εξωτερικού χρήστη',
  'trust.access.usersEmpty': 'Δεν υπάρχουν εξωτερικοί χρήστες ακόμη.',
  'trust.access.email': 'E-mail',
  'trust.access.name': 'Όνομα',
  'trust.access.company': 'Εταιρεία',
  'trust.access.role': 'Ρόλος',
  'trust.access.expiresInDays': 'Διάρκεια πρόσβασης (ημέρες)',
  'trust.access.namedAccess': 'Παραχώρηση πρόσβασης επώνυμου πελάτη',
  'trust.access.namedAccessHint': 'Επιτρέπει σε αυτόν τον χρήστη να δει τις πιο ευαίσθητες απαντήσεις μόνο για επώνυμους πελάτες.',
  'trust.access.status': 'Κατάσταση',
  'trust.access.expires': 'Λήγει',
  'trust.access.lastLogin': 'Τελευταία σύνδεση',
  'trust.access.nda': 'NDA',
  'trust.access.ndaAccepted': 'Αποδοχή {{date}}',
  'trust.access.ndaPending': 'Μη αποδεκτό',
  'trust.access.revoke': 'Ανάκληση',
  'trust.access.revokeConfirm': 'Ανάκληση πρόσβασης αυτού του χρήστη; Θα αποσυνδεθεί αμέσως.',
  'trust.access.save': 'Αποθήκευση',
  'trust.access.create': 'Δημιουργία',
  'trust.access.cancel': 'Άκυρο',
  'trust.access.statusINVITED': 'Προσκεκλημένος',
  'trust.access.statusACTIVE': 'Ενεργός',
  'trust.access.statusREVOKED': 'Ανακλήθηκε',
  'trust.access.statusExpired': 'Έληξε',
  'trust.access.role.CLIENT': 'Πελάτης',
  'trust.access.role.PARTNER': 'Συνεργάτης',
  'trust.access.role.AUDITOR': 'Ελεγκτής',
  'trust.access.passwordTitle': 'Αρχικός κωδικός',
  'trust.access.passwordBody':
    'Μοιραστείτε αυτόν τον κωδικό με τον/την {{email}} με ασφαλή τρόπο (εμφανίζεται μόνο μία φορά). Συνδέεται στον σύνδεσμο πύλης παρακάτω.',
  'trust.access.passwordDone': 'Έτοιμο',
  'trust.access.portalLink': 'Σύνδεσμος πύλης',
  'trust.access.usersLoadFailed': 'Δεν ήταν δυνατή η φόρτωση των εξωτερικών χρηστών.',

  'trust.access.requestsTitle': 'Αιτήματα πρόσβασης',
  'trust.access.requestsEmpty': 'Δεν υπάρχουν εκκρεμή αιτήματα πρόσβασης.',
  'trust.access.requestReason': 'Λόγος',
  'trust.access.approve': 'Έγκριση',
  'trust.access.deny': 'Απόρριψη',
  'trust.access.denyConfirm': 'Απόρριψη αυτού του αιτήματος πρόσβασης;',
  'trust.access.approveTitle': 'Έγκριση αιτήματος',
  'trust.access.requested': 'Αίτημα {{date}}',
  'trust.access.error.TRUST_EXTERNAL_USER_EXISTS': 'Υπάρχει ήδη εξωτερικός χρήστης με αυτό το e-mail.',
  'trust.access.error.UNKNOWN': 'Κάτι πήγε στραβά. Προσπαθήστε ξανά.',

  'trust.request.title': 'Αίτημα πρόσβασης',
  'trust.request.body':
    'Χρειάζεστε περισσότερες λεπτομέρειες; Ζητήστε ελεγχόμενη πρόσβαση στις προ-απαντημένες ερωτήσεις ασφάλειας αυτού του οργανισμού.',
  'trust.request.email': 'Το εταιρικό σας e-mail',
  'trust.request.company': 'Η εταιρεία σας',
  'trust.request.reason': 'Για ποιον λόγο χρειάζεστε πρόσβαση; (προαιρετικό)',
  'trust.request.submit': 'Αίτημα πρόσβασης',
  'trust.request.submitting': 'Αποστολή…',
  'trust.request.sent':
    'Ευχαριστούμε. Αν εγκριθεί η πρόσβαση, θα λάβετε πρόσκληση μέσω e-mail.',
  'trust.request.failed': 'Δεν ήταν δυνατή η αποστολή του αιτήματος. Προσπαθήστε αργότερα.',
  'trust.request.open': 'Έχετε ήδη πρόσβαση; Συνδεθείτε',

  'trust.portal.title': 'Πύλη ασφάλειας',
  'trust.portal.signIn': 'Σύνδεση',
  'trust.portal.signingIn': 'Σύνδεση…',
  'trust.portal.email': 'E-mail',
  'trust.portal.password': 'Κωδικός',
  'trust.portal.signInFailed': 'Το e-mail ή ο κωδικός δεν αναγνωρίστηκε, ή η πρόσβασή σας έληξε.',
  'trust.portal.signOut': 'Αποσύνδεση',
  'trust.portal.welcome': 'Βλέπετε τις απαντήσεις ασφάλειας του οργανισμού {{name}}.',
  'trust.portal.loadFailed': 'Δεν ήταν δυνατή η φόρτωση της πύλης.',
  'trust.portal.answersTitle': 'Προ-απαντημένες ερωτήσεις',
  'trust.portal.answersEmpty': 'Δεν υπάρχουν διαθέσιμες απαντήσεις για εσάς ακόμη.',
  'trust.portal.documentsTitle': 'Δημοσιευμένη θέση',
  'trust.portal.noDocuments': 'Δεν έχει δημοσιευτεί τίποτα στη δημόσια σελίδα.',
  'trust.portal.expiresOn': 'Η πρόσβασή σας λήγει στις {{date}}.',
  'trust.portal.readOnly': 'Μόνο για ανάγνωση. Οι απαντήσεις παρέχονται εμπιστευτικά.',

  'trust.portal.ndaTitle': 'Συμφωνία εμπιστευτικότητας',
  'trust.portal.ndaBody':
    'Ορισμένες απαντήσεις παρέχονται εμπιστευτικά. Συνεχίζοντας, συμφωνείτε να μην τις κοινοποιήσετε εκτός του οργανισμού σας και να τις χρησιμοποιήσετε μόνο για την αξιολόγηση αυτού του οργανισμού ως προμηθευτή ή συνεργάτη.',
  'trust.portal.ndaAccept': 'Συμφωνώ',
  'trust.portal.ndaLater': 'Όχι τώρα',
  'trust.portal.ndaUnlocks': 'Αποδεχθείτε τη συμφωνία εμπιστευτικότητας για να δείτε περισσότερες απαντήσεις.',
  'trust.portal.ndaAccepted': 'Η συμφωνία εμπιστευτικότητας έγινε αποδεκτή.',
};

let registered = false;
export function registerTrustPortalTranslations() {
  if (registered) return;
  registered = true;
  for (const [lng, table] of [['en', TRUST_PORTAL_STRINGS], ['el', TRUST_PORTAL_STRINGS_EL]] as const) {
    i18n.addResources(lng, 'translation', table as Record<string, string>);
  }
}
registerTrustPortalTranslations();

type TpKey = keyof typeof TRUST_PORTAL_STRINGS;
/** Base keys for the plural families, addressed without the _one/_other suffix. */
type TpPluralKey = 'trust.answers.seeded';

export function useTrustPortalT() {
  const { t, i18n: instance } = useTranslation();

  const tx = useCallback(
    (key: TpKey, options?: Record<string, unknown>): string =>
      t(key, { defaultValue: TRUST_PORTAL_STRINGS[key], ...options }) as string,
    [t],
  );

  const txn = useCallback(
    (key: TpPluralKey, count: number): string =>
      t(key, {
        count,
        defaultValue: TRUST_PORTAL_STRINGS[`${key}_${count === 1 ? 'one' : 'other'}` as TpKey],
      }) as string,
    [t],
  );

  const formatDate = useCallback(
    (value: string | Date | null | undefined): string => {
      if (!value) return '—';
      return new Intl.DateTimeFormat(instance.language, { dateStyle: 'medium' }).format(new Date(value));
    },
    [instance.language],
  );

  return { tx, txn, formatDate, locale: instance.language };
}
